import { useState, useEffect, useCallback, useRef } from 'react'
import { formatDateTime } from '../../utils/api'

/**
 * Notes: the one add + view panel for append-only staff notes. Deals and orders
 * both use it; each passes its own data source and the layout it wants.
 *
 *   sourceKey   what the notes belong to (a deal id, an order). When it changes
 *               the panel resets, and a reply for the old one is dropped.
 *   loadNotes   () => Promise<{ notes, partial? }>, notes oldest first.
 *   addNote     (text) => Promise<note | undefined>. A returned note is shown at
 *               once. With none, the list is loaded again.
 *   variant     'cards' (form on top, boxed notes) or 'thread' (avatar rows,
 *               input underneath).
 *
 * A note is { id, author, createdAt, text, kind }. Notes cannot be edited or
 * deleted. Failures show a visible error and block nothing silently.
 */

const PLAIN_KIND = 'note'
const AVATAR_COLOURS = ['#3a6ea5', '#7b5ea7', '#b5651d', '#2d7d6f', '#a64d79']
const ERROR_BOX_STYLE = {
  padding: 'var(--space-2) var(--space-3)',
  background: 'var(--color-error-bg, #fdecea)',
  color: 'var(--color-error, #b71c1c)',
  border: '1px solid var(--color-error, #b71c1c)',
  borderRadius: 6,
  fontSize: '0.8rem'
}
const MUTED_STYLE = { color: 'var(--color-text-muted)', fontSize: '0.85rem' }

// D1 CURRENT_TIMESTAMP is UTC "YYYY-MM-DD HH:MM:SS" with no zone marker.
// Normalise to ISO-UTC so it renders in the viewer's locale consistently.
function normaliseTimestamp(ts) {
  if (!ts) return ts
  const s = String(ts)
  if (s.includes('T') || s.endsWith('Z')) return s
  return `${s.replace(' ', 'T')}Z`
}

function initialsOf(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  const letters = words.length === 1 ? [words[0][0]] : [words[0][0], words[words.length - 1][0]]
  return letters.join('').toUpperCase()
}

function avatarColour(name) {
  const sum = Array.from(String(name || '')).reduce((total, ch) => total + ch.charCodeAt(0), 0)
  return AVATAR_COLOURS[sum % AVATAR_COLOURS.length]
}

export default function Notes({ sourceKey, loadNotes, addNote, variant = 'cards' }) {
  const [notes, setNotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  // The source could not look everything up and answered with only part of the
  // notes: say so rather than imply that is all.
  const [partial, setPartial] = useState(false)
  const [content, setContent] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  // Screens open the next record in place. A reply for the previous one must
  // not land on this one, and a half-typed note must not carry over.
  const currentKeyRef = useRef(sourceKey)
  currentKeyRef.current = sourceKey

  const load = useCallback(async () => {
    if (sourceKey == null || sourceKey === '') return
    setLoading(true)
    setLoadError(null)
    try {
      const res = await loadNotes()
      if (currentKeyRef.current !== sourceKey) return
      setNotes(res.notes || [])
      setPartial(res.partial === true)
    } catch (err) {
      if (currentKeyRef.current !== sourceKey) return
      setLoadError(err.message || 'Failed to load notes')
    } finally {
      if (currentKeyRef.current === sourceKey) setLoading(false)
    }
    // loadNotes is a fresh closure each render; sourceKey is what identifies the data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey])

  useEffect(() => {
    setContent('')
    setSubmitError(null)
    load()
  }, [load])

  const handleSubmit = async (e) => {
    e.preventDefault()
    const trimmed = content.trim()
    if (!trimmed || submitting) return

    setSubmitting(true)
    setSubmitError(null)
    try {
      const saved = await addNote(trimmed)
      if (currentKeyRef.current !== sourceKey) return
      setContent('')
      if (saved) setNotes((current) => [...current, saved])
      else await load()
    } catch (err) {
      setSubmitError(err.message || 'Failed to save note')
    } finally {
      setSubmitting(false)
    }
  }

  const form = (
    <NoteForm
      variant={variant}
      content={content}
      onContentChange={setContent}
      onSubmit={handleSubmit}
      submitting={submitting}
      error={submitError}
    />
  )
  const list = (
    <NoteList
      variant={variant}
      notes={notes}
      loading={loading}
      loadError={loadError}
      partial={partial}
      onRetry={load}
    />
  )

  if (variant === 'thread') return <div>{list}{form}</div>
  return <div>{form}{list}</div>
}

function NoteForm({ variant, content, onContentChange, onSubmit, submitting, error }) {
  const submitButton = (
    <button
      type="submit"
      className="btn btn-primary btn-sm"
      disabled={submitting || !content.trim()}
    >
      {submitting ? 'Saving…' : 'Add note'}
    </button>
  )
  const errorBox = error && <div style={{ marginTop: 'var(--space-2)', ...ERROR_BOX_STYLE }}>{error}</div>

  if (variant === 'thread') {
    return (
      <form onSubmit={onSubmit} style={{ marginTop: 'var(--space-2)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <input
            type="text"
            className="form-input"
            placeholder="Add a note for the team"
            aria-label="Add a note for the team"
            value={content}
            onChange={(e) => onContentChange(e.target.value)}
            disabled={submitting}
            style={{ flex: 1, fontSize: '0.85rem' }}
          />
          {submitButton}
        </div>
        {errorBox}
      </form>
    )
  }

  return (
    <form onSubmit={onSubmit}>
      <textarea
        className="form-input"
        rows={3}
        placeholder="Record what you did and why…"
        value={content}
        onChange={(e) => onContentChange(e.target.value)}
        disabled={submitting}
        style={{ width: '100%', fontSize: '0.85rem', resize: 'vertical' }}
      />
      {errorBox}
      <div style={{ marginTop: 'var(--space-2)', display: 'flex', justifyContent: 'flex-end' }}>
        {submitButton}
      </div>
    </form>
  )
}

function NoteList({ variant, notes, loading, loadError, partial, onRetry }) {
  const isThread = variant === 'thread'
  const wrapperStyle = isThread ? {} : { marginTop: 'var(--space-3)' }

  if (loading) return <div style={{ ...wrapperStyle, ...MUTED_STYLE }}>Loading notes…</div>
  if (loadError) {
    return (
      <div style={{
        ...wrapperStyle,
        ...ERROR_BOX_STYLE,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-2)'
      }}>
        <span>{loadError}</span>
        <button type="button" className="btn btn-xs btn-secondary" onClick={onRetry}>Retry</button>
      </div>
    )
  }

  const Row = isThread ? ThreadRow : CardRow
  return (
    <div style={wrapperStyle}>
      {partial && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-2)',
          color: 'var(--color-text-muted)',
          fontSize: '0.8rem'
        }}>
          <span>Could not check this quote's other ids. Some notes may be missing.</span>
          <button type="button" className="btn btn-xs btn-secondary" onClick={onRetry}>Retry</button>
        </div>
      )}
      {notes.length === 0 ? (
        <div style={MUTED_STYLE}>No notes yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: isThread ? 0 : 'var(--space-2)' }}>
          {notes.map((note, index) => <Row key={note.id} note={note} isFirst={index === 0} />)}
        </div>
      )}
    </div>
  )
}

function CardRow({ note }) {
  return (
    <div style={{
      padding: 'var(--space-2) var(--space-3)',
      background: 'var(--color-bg-secondary)',
      borderRadius: 6,
      border: '1px solid var(--color-border)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 'var(--space-2)',
        marginBottom: 4,
        fontSize: '0.75rem',
        color: 'var(--color-text-muted)'
      }}>
        <strong style={{ color: 'var(--color-text)' }}>{note.author || 'Unknown'}</strong>
        <span>{formatDateTime(normaliseTimestamp(note.createdAt))}</span>
      </div>
      <div style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{note.text}</div>
    </div>
  )
}

function ThreadRow({ note, isFirst }) {
  const author = note.author || 'Unknown'
  const when = formatDateTime(normaliseTimestamp(note.createdAt))
  const kindLabel = note.kind && note.kind !== PLAIN_KIND ? ` · ${note.kind.replace(/_/g, ' ')}` : ''
  return (
    <div style={{
      display: 'flex',
      gap: 10,
      padding: '8px 0',
      borderTop: isFirst ? 'none' : '1px solid var(--color-border)'
    }}>
      <span
        aria-hidden="true"
        style={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          background: avatarColour(author),
          color: '#fff',
          display: 'grid',
          placeItems: 'center',
          fontSize: 11,
          fontWeight: 700,
          flex: 'none'
        }}
      >
        {initialsOf(author)}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '0.85rem' }}>
          <strong>{author}</strong>{' '}
          <span style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>{when}{kindLabel}</span>
        </div>
        <div style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{note.text}</div>
      </div>
    </div>
  )
}
